<template>
  <div style="height: 600px">
    <ag-grid-vue
      style="width: 100%; height: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="myGrid"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      :fullWidthCellRenderer="fullWidthCellRenderer"
      :isFullWidthCell="isFullWidthCell"
      :getRowHeight="getRowHeight"
      :rowSelection="rowSelection"
      :isRowSelectable="isRowSelectable"
      :isExternalFilterPresent="isExternalFilterPresent"
      :doesExternalFilterPass="doesExternalFilterPass"
      @first-data-rendered="firstDataRendered"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import agGrid from "@/mixins/agGrid.js";
import { date } from "@/components/agGrid/cellRenderers";
import carts from "@/data/carts.json";
import CustomerOrdersOrderLink from "./CustomerOrdersOrderLink";
import CustomerOrdersWebOrderLink from "./CustomerOrdersWebOrderLink";

export default {
  name: "CustomerOrders",
  data() {
    return {
      columnDefs: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      fullWidthCellRenderer: null,
      isFullWidthCell: null,
      getRowHeight: null,
      rowSelection: null,
      isRowSelectable: null,
      rowDetails: -1,
    };
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  components: {
    AgGridVue,
    CustomerOrdersOrderLink, // eslint-disable-line
    CustomerOrdersWebOrderLink, // eslint-disable-line
  },
  mixins: [agGrid],
  methods: {
    firstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
    statusSpan(params) {
      const colors = {
        partial: "blue",
        complete: "green",
      };
      const style = `inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium leading-4 bg-${
        colors[params.value]
      }-100 text-${colors[params.value]}-800`;
      return `<span class="${style}">${params.value}</span>`;
    },
    shipDate(params) {
      let date = new Date(params.value);
      return `${date
        .getMonth()
        .toString()
        .padStart(2, "0")}/${date
        .getDate()
        .toString()
        .padStart(2, "0")}/${date.getFullYear()}`;
    },

    // This is always set to true because we want to hide the
    // interlaced OrderDetails rows unless a row is selected
    isExternalFilterPresent() {
      return true;
    },

    externalFilterChanged(selectedRow) {
      this.rowDetails = selectedRow;
      this.gridApi.onFilterChanged();
    },

    doesExternalFilterPass(rowNode) {
      if (rowNode.data.fullWidth) {
        if (Number(this.rowDetails) + 1 === Number(rowNode.id)) {
          return true;
        }
        return false;
      }
      return true;
    },

    detailIsNotAdjustment(detail) {
      return detail.Adjustment == 0;
    },
  },
  beforeMount() {
    this.gridOptions = {};
    this.context = { componentParent: this };
    this.rowSelection = "single";

    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 1,
      suppressMovable: true,
    };

    this.isFullWidthCell = (rowNode) => {
      return rowNode.data.fullWidth;
    };

    this.isRowSelectable = (rowNode) => {
      return !rowNode.data.fullWidth;
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "CustomerOrdersWebOrderLink",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        cellRenderer: "dateCell",
      },
      {
        headerName: "Cart",
        field: "shopping_cart",
        filterParams: this.customFilter({
          options: carts.map((cart) => {
            return { key: cart.ID, value: cart.CartName };
          }),
        }),
        sortable: false,
        cellRenderer: (params) => params.data.shopping_cart.ID,
      },
      {
        headerName: "Ship City",
        field: "ShipCity",
      },
      {
        headerName: "Ship State",
        field: "ShipState",
      },
    ];

    this.frameworkComponents = {
      dateCell: date,
    };

    (this.onRowDataChanged = () => {
      this.rowDetails = 1;
      this.context.componentParent.gridApi.onFilterChanged();
    }),
      (this.fullWidthCellRenderer = (params) => {

        let details = params.data?.details || [];

        details = details.map((detail) => {
            if(this.detailIsNotAdjustment(detail))
              return detail.Product;
          })
          .filter((detail) => detail !== undefined);

        const detailsList = ! details.length ? ["No Products"] : details;

        var eDiv = document.createElement("div");

        eDiv.innerHTML =
          '<div class="bg-gray-100 py-1 px-2 h-full">' +
            detailsList.join(" <br> ") +
          "</div>";
        return eDiv.firstChild;
      });

    this.getRowHeight = (params) => {
      if (params.data.fullWidth) {
        const productCount = params.data.details?.filter( detail => this.detailIsNotAdjustment(detail)).length || 1;
        switch (productCount) {
          case 1:
            return 26
          case 2:
            return 40
          default:
            return 17.5 * productCount
        }
      }
    };
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  computed: {
    rowData: {
      get() {
        const orders = this.$store.getters["customers/getOrders"];

        // We need to interlace the Orders & OrderDetails information for fullwidth details
        let newData = [];
        orders.forEach((order) => {
          newData.push(order);

          newData.push({
            ...order,
            fullWidth: true,
          });
        });

        return newData;
      },
    },
  },
};
</script>

<style lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.ag-header-cell-label .ag-header-cell-text {
  white-space: normal !important;
  text-align: center;
}
.ag-theme-balham .ag-row:hover {
  background-color: #def7ec !important;
  color: black;
}
.ag-theme-balham .ag-row-selected {
  background-color: #bcf0da !important;
  color: black;
}
</style>
